import type { SurveyForRespondent } from '@voice2me/shared';
import { useMutation, useQuery } from '@tanstack/react-query';
import { useCallback, useRef, useState } from 'react';
import { Navigate, useParams } from 'react-router-dom';
import { ApiError, api } from '../api/client.js';
import { useSession } from '../auth/useSession.js';
import { AnonymityBanner } from '../survey/AnonymityBanner.js';
import { CompletionMessage } from '../survey/CompletionMessage.js';
import { SurveyForm } from '../survey/SurveyForm.js';
import { useDraftAnswers } from '../survey/useDraftAnswers.js';

type FieldErrors = Record<string, string>;

/** Renders a millisecond span as "X mins Y secs" for the completion screen, pluralised and rounded. */
function formatDuration(ms: number): string {
  const totalSeconds = Math.max(0, Math.round(ms / 1000));
  const mins = Math.floor(totalSeconds / 60);
  const secs = totalSeconds % 60;
  const unit = (value: number, word: string) => `${value} ${word}${value === 1 ? '' : 's'}`;
  if (mins === 0) return unit(secs, 'sec');
  return `${unit(mins, 'min')} ${unit(secs, 'sec')}`;
}

export function SurveyPage() {
  const { vendorSlug = '', surveySlug = '' } = useParams();
  const session = useSession();
  const surveyKey = `${vendorSlug}/${surveySlug}`;

  const { answers, setAnswer, clearDraft } = useDraftAnswers(surveyKey);
  const [consent, setConsent] = useState(false);
  const [acknowledged, setAcknowledged] = useState(false);
  const [fieldErrors, setFieldErrors] = useState<FieldErrors>({});
  // Wall-clock time (ms) the first question appeared, and the span to submit. Kept in a ref so it
  // survives re-renders without restarting; the elapsed value is frozen at submit time.
  const startedAtRef = useRef<number | null>(null);
  const [elapsedMs, setElapsedMs] = useState<number | null>(null);
  // Called by the form the first time the respondent reaches a question; starts the timer once.
  const handleSurveyStart = useCallback(() => {
    if (startedAtRef.current === null) startedAtRef.current = Date.now();
  }, []);

  const survey = useQuery({
    queryKey: ['survey', surveyKey],
    enabled: Boolean(session.data),
    retry: false,
    queryFn: () => api.get<SurveyForRespondent>(`/s/${vendorSlug}/${surveySlug}`),
  });

  const submit = useMutation({
    mutationFn: () =>
      api.post<{ ok: true }>(`/s/${vendorSlug}/${surveySlug}/submit`, {
        answers,
        consentToIdentify: consent,
      }),
    onSuccess: () => {
      clearDraft();
      setFieldErrors({});
      setElapsedMs(startedAtRef.current != null ? Date.now() - startedAtRef.current : null);
    },
    onError: (error) => {
      // The server returns per-question errors; surface them next to the questions themselves.
      if (error instanceof ApiError && Array.isArray(error.details)) {
        const mapped: FieldErrors = {};
        for (const detail of error.details as Array<{ questionId: string; message: string }>) {
          mapped[detail.questionId] = detail.message;
        }
        setFieldErrors(mapped);
      }
    },
  });

  if (session.isPending) return <main>Loading…</main>;

  if (!session.data) {
    return <Navigate to={`/login?redirectTo=${encodeURIComponent(`/${surveyKey}`)}`} replace />;
  }

  if (survey.isPending) return <main>Loading…</main>;

  if (survey.isError) {
    const status = survey.error instanceof ApiError ? survey.error.status : 0;
    return (
      <main>
        <h1>{status === 403 ? 'Not open to your account' : 'Survey not found'}</h1>
        <p>
          {status === 403
            ? 'This survey is for a different company. If you think that is wrong, contact whoever sent you the link.'
            : 'That link does not exist, or the survey it pointed to has been removed.'}
        </p>
      </main>
    );
  }

  const { definition, anonymityMode, status, vendorName, vendorLogoUrl, alreadyCompleted } =
    survey.data;

  if (submit.isSuccess) {
    return (
      <main>
        <h1>Thank you</h1>
        <CompletionMessage message={definition.completionMessage} />
        {definition.showCompletionTime && elapsedMs != null && (
          <p className="completion-time">Completed in: {formatDuration(elapsedMs)}</p>
        )}
      </main>
    );
  }

  if (alreadyCompleted) {
    return (
      <main>
        <h1>Already completed</h1>
        <p>You have already answered this survey. Responses cannot be changed after submitting.</p>
      </main>
    );
  }

  if (status !== 'published') {
    return (
      <main>
        <h1>{status === 'closed' ? 'This survey has closed' : 'Not open yet'}</h1>
        <p>
          {status === 'closed'
            ? 'Thank you for your interest. This survey is no longer accepting responses.'
            : 'This survey is still being prepared. You are seeing it because you can edit it.'}
        </p>
      </main>
    );
  }

  const generalError =
    submit.isError && Object.keys(fieldErrors).length === 0 ? submit.error.message : null;

  const submitFooter = (
    <>
      {generalError && <p role="alert">{generalError}</p>}
      {Object.keys(fieldErrors).length > 0 && (
        <p role="alert">Please check the highlighted questions.</p>
      )}
      <button type="submit" disabled={submit.isPending || !acknowledged}>
        {submit.isPending ? 'Submitting…' : 'Submit my answers'}
      </button>
      <p className="note">Your answers are kept on this device until you submit.</p>
    </>
  );

  return (
    <main>
      {vendorLogoUrl && (
        <img className="survey-logo" src={vendorLogoUrl} alt={`${vendorName} logo`} />
      )}
      <p className="vendor">{vendorName}</p>
      <h1>{definition.title}</h1>
      {/* In slideshow mode the introduction is the first slide, so don't also show it here. */}
      {definition.displayMode !== 'slideshow' && definition.description && (
        <p className="description">{definition.description}</p>
      )}

      {/* In slideshow mode the notice is the opening slide instead, so it isn't repeated here. */}
      {definition.displayMode !== 'slideshow' && (
        <AnonymityBanner
          mode={anonymityMode}
          consent={consent}
          onConsentChange={setConsent}
          acknowledged={acknowledged}
          onAcknowledgeChange={setAcknowledged}
          agreement={definition.agreement}
        />
      )}

      <form
        onSubmit={(event) => {
          event.preventDefault();
          submit.mutate();
        }}
        noValidate
      >
        <SurveyForm
          definition={definition}
          answers={answers}
          onAnswerChange={setAnswer}
          errors={fieldErrors}
          disabled={submit.isPending}
          footer={submitFooter}
          onStart={handleSurveyStart}
          introBanner={
            definition.displayMode === 'slideshow' ? (
              <AnonymityBanner
                mode={anonymityMode}
                consent={consent}
                onConsentChange={setConsent}
                acknowledged={acknowledged}
                onAcknowledgeChange={setAcknowledged}
                agreement={definition.agreement}
              />
            ) : undefined
          }
          canAdvance={acknowledged}
        />
      </form>
    </main>
  );
}
